import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Grid,
  Typography,
  Container,
  Tab,
  Tabs,
  useTheme,
  IconButton,
  Box,
  Paper,
  Button,
  Divider,
  FormControl,
} from "@mui/material";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { PhoneAndroid, AccessTime, Email } from "@mui/icons-material";
import TimezoneSelector from "../../../util/timezoneSelector";
import { GetTimezoneInfo } from "../../../util/timezones";
import { UserContext } from "../../../components/auth/auth-context";
import { GetSortedWorkingHours } from "../../../util/workingHour";
import BusinessBookingTypeList from "./businessBookingTypeList";
import { GET_BUSINESS_BY_URL } from "../../../graphql/queries";
import BookingTypeStyle from "../business/businessBookingType.styles";
import officePlaceHolderImage from "../../../assets/images/business-image-placeholder.png";
import BusinessAddressLink from "../../../util/google/businessAddressLink";
import MapWithBusinessAddress from "./mapWithBusinessAddress";
// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// This component will set the view of the map (used in MapWithBusinessAddress if needed)
function SetMapView({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    map.setView(center, zoom);
  }, [center, zoom, map]);
  return null;
}

export const BusinessBookingTypes = () => {

  const userContext = useContext(UserContext);
  const providerUser = userContext.providerUser;

  const theme = useTheme();
  const styles = BookingTypeStyle(theme);
  const { businessUrl, staffUrl } = useParams();
  const [business, setBusiness] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedTimeZone, setSelectedTimeZone] = useState(GetTimezoneInfo(providerUser?.timezone)?.timezone);

  const { loading: loadingBusiness, error: errorBusiness, data: dataBusiness } = useQuery(GET_BUSINESS_BY_URL, {
    variables: { businessUrl: businessUrl },
  });

  useEffect(() => {
    if (dataBusiness && dataBusiness.businessByUrl) {
      setBusiness({ ...dataBusiness.businessByUrl, businessBasic: dataBusiness.businessByUrl, url: businessUrl });
    }
  }, [dataBusiness]);

  const handleTimezoneSelect = useCallback((timezone) => {
    setSelectedTimeZone(timezone);
  }, []);

  if (loadingBusiness) return <Box>Loading...</Box>;
  if (errorBusiness) return <Box>Error: {errorBusiness.message}</Box>;
  if (!business) return null;

  const handleBack = () => window.history.back();
  const handleTabChange = (event, newValue) => setSelectedTab(newValue);

  const sampleImages = business.images
    .filter((item) => Number(item?.businessId) === Number(business.id))
    .map((item) => item?.url);

  const handlePreviousImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? sampleImages.length - 1 : prevIndex - 1
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === sampleImages.length - 1 ? 0 : prevIndex + 1
    );
  };

  // Find the working hour group that is marked as default
  const workingHourGroup = business.workingHourGroups.find(group => group.def === true) || business.workingHourGroups[0];
  const sortedWorkingHours = GetSortedWorkingHours(
    workingHourGroup?.workingHours || [],
    selectedTimeZone ?? workingHourGroup?.timeZone
  );

  return (
    <Container maxWidth="lg" sx={styles.root}>
      <Box sx={styles.backLink} onClick={handleBack}>
        <FaChevronLeft style={styles.backIcon} />
        <Typography variant="subtitle1">Back</Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={styles.imageContainer}>
            <Box sx={styles.imageWrapper}>
              <IconButton onClick={handlePreviousImage} sx={styles.imageNavButtonLeft}>
                <FaChevronLeft />
              </IconButton>
              <img 
                src={
                  sampleImages && sampleImages.length > 0
                    ? sampleImages[currentImageIndex]
                    : officePlaceHolderImage
                }
                alt={
                  sampleImages && sampleImages.length > 0
                    ? `Business ${currentImageIndex + 1}`
                    : "Office Placeholder"
                }
                style={styles.businessImage}
              />
              <IconButton onClick={handleNextImage} sx={styles.imageNavButtonRight}>
                <FaChevronRight />
              </IconButton>
            </Box>
          </Paper>

          <Box sx={styles.tabsContainer}>
            <Tabs value={selectedTab} onChange={handleTabChange}>
              <Tab label="Booking Types" />
            </Tabs>
          </Box>

          <Box>
            <BusinessBookingTypeList business={business} url={business?.businessBasic?.url ?? business?.url} />
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={styles.businessDetails}>
            <Typography variant="h5" component="h2" gutterBottom>
              {business.businessBasic.name}
            </Typography>
            <Typography variant="body1" paragraph>
              {business.businessBasic.description}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              <BusinessAddressLink address={business.businessBasic.address} />
            </Typography>
            {/* Replace static map section with the dynamic MapWithBusinessAddress */}
            {/* <MapWithBusinessAddress business={business} styles={styles} /> */}
          </Paper>

          <Paper elevation={3} sx={styles.aboutUs}>
            <Typography variant="h6" gutterBottom>
              Contact Us
            </Typography>
            <Typography variant="body1" paragraph>
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  <PhoneAndroid sx={{ marginRight: theme.spacing(1) }} /> {business.businessBasic.phoneNumbers[0].phoneNumber}
                </span>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => window.location.href = `tel:${business.businessBasic.phoneNumbers[0].phoneNumber}`}
                >
                  Call Us
                </Button>
              </span>
            </Typography>

            <Typography variant="body1" paragraph>
              <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  <Email sx={{ marginRight: theme.spacing(1) }} /> {business.businessBasic.emails[0]}
                </span>
              </span>
            </Typography>
          </Paper>

          {/* Working Hours section */}
          <Paper elevation={3} sx={styles.workingHours}>
            <Typography variant="h6" gutterBottom>
              Working Hours
            </Typography>
            <>
              <FormControl fullWidth>
                <TimezoneSelector
                  timezoneId={selectedTimeZone ?? workingHourGroup?.timeZone}
                  onTimezoneSelect={handleTimezoneSelect}
                />
              </FormControl>
              {sortedWorkingHours.map((hour) => {
                let parentHour = hour.parent;
                let childrenHour = hour.children;
                // Check if the current hour is a parent (i.e., has no parentId)
                if (parentHour) {
                  return (
                    <div key={parentHour.id}>
                      <Typography
                        variant="body1"
                        paragraph
                        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center' }}>
                          <AccessTime sx={{ marginRight: theme.spacing(1) }} /> {parentHour.day}
                        </span>
                        <span>
                          {parentHour.onJob ? `${parentHour.startTime} - ${parentHour.endTime}` : 'Closed'}
                        </span>
                      </Typography>
                      {/* Render child working hours without the hour icon */}
                      {childrenHour.map(child => (
                        <Typography
                          key={child.id}
                          variant="body2"
                          paragraph
                          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingLeft: '20px' }} // Indent child items
                        >
                          <span style={{ display: 'flex', alignItems: 'center' }}>
                            {child.day}
                          </span>
                          <span>
                            {child.onJob ? `${child.startTime} - ${child.endTime}` : 'Closed'}
                          </span>
                        </Typography>
                      ))}
                      <Divider sx={{ margin: '16px 0' }} />
                    </div>
                  );
                }
                return null;
              })}
            </>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default BusinessBookingTypes;
